/**
 * Australian Region Default Preferences
 * 
 * Provides Australian-first default preferences for new users
 */

import type { UserPreferences, DashboardPreferences } from '../../../types/preference.types';

/**
 * Default dashboard preferences (all components visible)
 */
const DEFAULT_DASHBOARD_PREFERENCES: DashboardPreferences = {
  showSpendingChart: true,
  showRecentTransactions: true,
  showBudgetOverview: true,
};

/**
 * Get default preferences for Australian region
 * 
 * Returns preferences with Australian-first defaults:
 * - Currency: AUD
 * - Date Format: DD/MM/YYYY (Australian format)
 * - Financial Year: July 1st - June 30th
 * - Timezone: Australia/Sydney
 * - Theme: Light mode
 * - Pay Day fields: null (opt-in)
 * 
 * @returns Partial UserPreferences object with default values (excludes id, userId, timestamps)
 */
export function getDefaultPreferences(): Omit<UserPreferences, 'id' | 'userId' | 'createdAt' | 'updatedAt'> {
  return {
    language: 'en',
    currency: 'AUD',
    currencyDisplay: 'symbol',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    timezone: 'Australia/Sydney',
    financialYearStart: 'July',
    theme: 'light',
    dashboardPreferences: DEFAULT_DASHBOARD_PREFERENCES,
    transactionRetentionYears: null, // Keep forever by default
    payFrequency: null,
    payDay: null,
    payAnchorDate: null,
    estimatedNetPay: null,
    payTransactionName: null,
    gstRegistered: false,
    businessName: null,
    businessAbn: null,
  };
}
